'use client'

import { useEffect, useState } from 'react'

const breakpoints = ['sm', 'md', 'lg', 'xl', '2xl'] as const
export type Breakpoint = (typeof breakpoints)[number]

export type BreakpointState = Record<Breakpoint, boolean>

function getBreakpointValue(breakpoint: Breakpoint): string {
  if (typeof window === 'undefined') return '0px'
  return getComputedStyle(document.documentElement)
    .getPropertyValue(`--breakpoint-${breakpoint}`)
    .trim()
}

/**
 * Returns the current breakpoint state for the screen. Each breakpoint is a boolean value indicating if the screen is at or above the breakpoint.
 *
 * @returns An object with the current breakpoint state for the screen.
 * The values will be true if the screen is at or above the breakpoint, false otherwise.
 *
 * default tailwind breakpoints:
 * --breakpoint-sm: 40rem; (640px)
 * --breakpoint-md: 48rem; (768px)
 * --breakpoint-lg: 64rem; (1024px)
 * --breakpoint-xl: 80rem; (1280px)
 * --breakpoint-2xl: 96rem; (1536px)
 */
export function useBreakpoint() {
  const [state, setState] = useState<BreakpointState>({
    sm: false,
    md: false,
    lg: false,
    xl: false,
    '2xl': false,
  })

  useEffect(() => {
    const mediaQueries = breakpoints.map((bp) => ({
      breakpoint: bp,
      mql: window.matchMedia(`(min-width: ${getBreakpointValue(bp)})`),
    }))

    const update = () => {
      const newState = {} as BreakpointState
      for (const { breakpoint, mql } of mediaQueries) {
        newState[breakpoint] = mql.matches
      }
      setState(newState)
    }

    update()

    for (const { mql } of mediaQueries) {
      mql.addEventListener('change', update)
    }

    return () => {
      for (const { mql } of mediaQueries) {
        mql.removeEventListener('change', update)
      }
    }
  }, [])

  return state
}
